import React, { useEffect, useState, useReducer, useRef } from "react";
import {
  fetchPendingOrders,
  declineOrder,
  lookupAppointment,
} from "../../libs/requests/orderSearch";
import postZenOrder from "../../libs/requests/postZenOrder";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useHistory } from "react-router-dom";
import {
  Table,
  Button,
  Message,
  Icon,
  Loader,
  Segment,
  Dimmer,
} from "semantic-ui-react";
import { createRequestBody, isOnboarded, checkUsernameValid } from "../../libs/utils";
import makeRequest from "../../libs/makeRequest";
import { BASE_URL } from "../../libs/environment";

const AVAILABILITY_TTL_MS = 20 * 60 * 1000;
const USERNAME_DEBOUNCE_MS = process.env.NODE_ENV === 'test' ? 0 : 500;

async function retryAsync(fn, maxAttempts = 3) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === maxAttempts) throw err;
      const delay = process.env.NODE_ENV === 'test' ? 0 : 1000 * attempt;
      await new Promise(r => setTimeout(r, delay));
    }
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_DATA":
      return {
        ...state,
        data: action.data,
        originalData: action.data,
      };
    case "UPDATE_ORDER_AVAILABILITY":
      return {
        ...state,
        data: state.data.map(order =>
          order.reference === action.payload.reference
            ? {
                ...order,
                availabilityReference: action.payload.availabilityReference,
                lastRefreshed: action.payload.lastRefreshed
              }
            : order
        ),
        originalData: state.originalData.map(order =>
          order.reference === action.payload.reference
            ? {
                ...order,
                availabilityReference: action.payload.availabilityReference,
                lastRefreshed: action.payload.lastRefreshed
              }
            : order
        )
      };
    case "CHANGE_SORT":
      if (state.column === action.column) {
        return {
          ...state,
          data: [...state.data].reverse(),
          direction:
            state.direction === "ascending" ? "descending" : "ascending",
        };
      }
      return {
        column: action.column,
        data: [...state.originalData].sort((a, b) =>
          (a[action.column] ?? "")
            .toString()
            .localeCompare((b[action.column] ?? "").toString())
        ),
        originalData: state.originalData,
        direction: "ascending",
      };
    default:
      return state;
  }
}

function cleanObject(obj) {
  if (!obj || typeof obj !== "object") return undefined;
  const cleaned = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = cleanObject(value);
      if (nested !== undefined && Object.keys(nested).length > 0) {
        cleaned[key] = nested;
      }
    } else if (Array.isArray(value)) {
      if (value.length > 0) cleaned[key] = value;
    } else if (value !== "") {
      cleaned[key] = value;
    }
  });
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function buildPayload(order, availabilityReference, preferredDate, preferredUsername) {
  const activationDate = preferredDate
    ? preferredDate.toISOString()
    : order.preferredActivationDate;

  const rawJavaObject = {
    availabilityReference,
    ...(order.numberPortAvailabilityReference && {
      numberPortAvailabilityReference: order.numberPortAvailabilityReference
    }),
    ...(order.installationDetails && {
      installationDetails: {
        ...(order.installationDetails.phoneNumber && {
          phoneNumber: order.installationDetails.phoneNumber
        }),
        ...(order.installationDetails.goldAddressKey && {
          goldAddressKey: order.installationDetails.goldAddressKey
        }),
        ...(order.installationDetails.districtCode && {
          districtCode: order.installationDetails.districtCode
        }),
        ...(order.installationDetails.address && {
          address: {
            ...(order.installationDetails.address.organisationName && {
              organisationName: order.installationDetails.address.organisationName
            }),
            ...(order.installationDetails.address.poBox && {
              poBox: order.installationDetails.address.poBox
            }),
            ...(order.installationDetails.address.subPremiseName && {
              subPremiseName: order.installationDetails.address.subPremiseName
            }),
            ...(order.installationDetails.address.premiseName && {
              premiseName: order.installationDetails.address.premiseName
            }),
            ...(order.installationDetails.address.thoroughfareNumber && {
              thoroughfareNumber: order.installationDetails.address.thoroughfareNumber
            }),
            ...(order.installationDetails.address.dependentThoroughfare && {
              dependentThoroughfare: order.installationDetails.address.dependentThoroughfare
            }),
            ...(order.installationDetails.address.thoroughfare && {
              thoroughfare: order.installationDetails.address.thoroughfare
            }),
            ...(order.installationDetails.address.doubleDependentLocality && {
              doubleDependentLocality: order.installationDetails.address.doubleDependentLocality
            }),
            ...(order.installationDetails.address.locality && {
              locality: order.installationDetails.address.locality
            }),
            ...(order.installationDetails.address.postTown && {
              postTown: order.installationDetails.address.postTown
            }),
            ...(order.installationDetails.address.county && {
              county: order.installationDetails.address.county
            }),
            ...(order.installationDetails.address.country && {
              country: order.installationDetails.address.country
            }),
            ...(order.installationDetails.address.postCode && {
              postCode: order.installationDetails.address.postCode
            })
          }
        }),
        ...(order.installationDetails.uprn && {
          uprn: order.installationDetails.uprn
        })
      }
    }),
    ...(order.productCode && { productCode: order.productCode }),
    ...(order.options && order.options.length > 0 && { options: order.options }),
    ...(activationDate && { preferredActivationDate: activationDate }),
    ...(order.orderReference && { orderReference: order.orderReference }),
    ...(order.rid && { rid: order.rid }),
    ...(order.linkedOrderReference && {
      linkedOrderReference: order.linkedOrderReference
    }),
    ...(order.appointment && order.appointment.appointmentType && {
      appointment: {
        ...(order.appointment.hazardNotes && {
          hazardNotes: order.appointment.hazardNotes
        }),
        ...(order.appointment.notes && { notes: order.appointment.notes }),
        ...(order.appointment.specialArrangementNotes && {
          specialArrangementNotes: order.appointment.specialArrangementNotes
        }),
        ...(order.appointment.timeSlot !== undefined && {
          timeSlot: order.appointment.timeSlot
        }),
        appointmentType: order.appointment.appointmentType
      }
    }),
    ...(order.endUserContact && {
      endUserContact: {
        ...(order.endUserContact.fullName && {
          fullName: order.endUserContact.fullName
        }),
        ...(order.endUserContact.phoneNumber && {
          phoneNumber: order.endUserContact.phoneNumber
        })
      }
    }),
    ...(order.emailNotificationsOptIn && {
      emailNotificationsOptIn: {
        ...(order.emailNotificationsOptIn.emailNotifications !== undefined && {
          emailNotifications: order.emailNotificationsOptIn.emailNotifications
        }),
        ...(order.emailNotificationsOptIn.emailAddresses &&
          order.emailNotificationsOptIn.emailAddresses.length > 0 && {
            emailAddresses: order.emailNotificationsOptIn.emailAddresses
          })
      }
    }),
    ...(order.ontDetails && {
      ontDetails: {
        ...(order.ontDetails.ontReference && {
          ontReference: order.ontDetails.ontReference
        }),
        ...(order.ontDetails.port !== undefined && {
          port: order.ontDetails.port
        })
      }
    }),
    ...(order.broadbandCredentials && {
      broadbandCredentials: {
        ...((preferredUsername || order.broadbandCredentials.username) && {
          username: preferredUsername || order.broadbandCredentials.username
        }),
        ...(order.broadbandCredentials.password && {
          password: order.broadbandCredentials.password
        }),
        ...(order.broadbandCredentials.numberOfIps !== undefined && {
          numberOfIps: order.broadbandCredentials.numberOfIps
        })
      }
    }),
    ...(order.realms && order.realms.length > 0 && { realms: order.realms }),
    ...(order.accessLineId && { accessLineId: order.accessLineId }),
    ...(order.newSsramsRequired && {
      newSsramsRequired: order.newSsramsRequired
    }),
    ...(order.workingLineTakeover !== undefined && {
      workingLineTakeover: order.workingLineTakeover
    }),
    ...(order.cellularDetails && {
      cellularDetails: {
        ...(order.cellularDetails.iccId && {
          iccId: order.cellularDetails.iccId
        })
      }
    }),
    ...(order.contractTerm !== undefined && {
      contractTerm: parseInt(order.contractTerm, 10)
    }),
    ...(order.pricingOption !== undefined && {
      pricingOption: order.pricingOption
    }),
    ...(order.pricingOption === "bespoke" &&
      order.bespokePrice !== undefined && {
        bespokePrice: order.bespokePrice
      }),
    ...(order.router !== undefined && { router: order.router }),
    ...(order.deliveryOptions !== undefined && { deliveryOptions: order.deliveryOptions }),
    ...(order.bureauEndCustomer !== undefined && { bureauEndCustomer: order.bureauEndCustomer }),
    ...(order.isBureauReseller !== undefined && { isBureauReseller: order.isBureauReseller })
  };

  return cleanObject(rawJavaObject);
}

function renderIfExists(value, label) {
  return value !== undefined && value !== null && value !== "" ? (
    <>
      <strong>{label}:</strong> {value.toString()}
      <br />
    </>
  ) : null;
}

function renderAddressIfExists(address) {
  if (!address) return null;
  const addressParts = [
    address.organisationName,
    address.poBox,
    address.subPremiseName,
    address.premiseName,
    address.thoroughfareNumber && address.thoroughfare
      ? `${address.thoroughfareNumber} ${address.thoroughfare}`
      : address.thoroughfareNumber || address.thoroughfare,
    address.dependentThoroughfare,
    address.doubleDependentLocality,
    address.locality,
    address.postTown,
    address.county,
    address.country,
    address.postCode,
  ].filter((part) => part && part.trim() !== "");

  return addressParts.length > 0 ? (
    <>
      <strong>Address:</strong>
      <br />
      {addressParts.join(", ")}
      <br />
    </>
  ) : null;
}

function createDevMock(scenario) {
  let callCount = 0;
  return async () => {
    callCount++;
    switch (scenario) {
      case 'success':
        return 'DEV_ZEN_REF_001';
      case 'appointment_error':
        throw 'Appointment date unavailable. Please select an alternative appointment date.';
      case 'stale_retry_success':
        if (callCount === 1) throw 'The availability may have become stale or an error occurred while placing the order. Please try again.';
        return 'DEV_ZEN_RETRY_OK';
      case 'stale_retry_fail':
        throw 'The availability may have become stale or an error occurred while placing the order. Please try again.';
      case 'validation_errors':
        throw [{ message: 'Invalid address details' }, { message: 'Product not available at this address' }];
      case 'server_error':
        throw 'An error occurred on the server, please contact support for assistance.';
      default:
        return null;
    }
  };
}

export default function PendingOrders() {
  const [placingOrder, setPlacingOrder] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState([]);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [showRetryMessage, setShowRetryMessage] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [decliningOrder, setDecliningOrder] = useState(null);
  const [availabilityRefreshStatus, setAvailabilityRefreshStatus] = useState({});
  const [refreshMessage, setRefreshMessage] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [dateOverrides, setDateOverrides] = useState({});
  const [appointmentSlots, setAppointmentSlots] = useState({});
  const [usernameOverrides, setUsernameOverrides] = useState({});
  const [usernameMessages, setUsernameMessages] = useState({});
  const usernameCheckTimer = useRef({});
  const [devScenario, setDevScenario] = useState('');
  const [devLastPayload, setDevLastPayload] = useState(null);
  const [showDevPayload, setShowDevPayload] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(
    () => sessionStorage.getItem('devMode') === '1'
  );

  const history = useHistory();

  const [state, dispatch] = useReducer(reducer, {
    column: null,
    direction: null,
    data: [],
    originalData: [],
  });

  const refreshOrderAvailability = async (order) => {
    const { installationDetails } = order;
    const address = installationDetails?.address;
    const districtCode = installationDetails?.districtCode;
    const goldAddressKey = installationDetails?.goldAddressKey;
    const uprn = installationDetails?.uprn;
    const postCode = address?.postCode;

    try {
      setAvailabilityRefreshStatus(prev => ({
        ...prev,
        [order.reference]: { status: 'loading' }
      }));

      const isZen = await isOnboarded('Zen');
      const shouldMakeRequest = isZen && (districtCode || uprn);
      const uprnRequest = !goldAddressKey && isZen && uprn;

      const fetchAvailability = () => {
        if (!shouldMakeRequest) {
          return Promise.resolve({ availabilityReference: 'n/a', lineDetails: {} });
        }
        const addressReference = {
          addressReferenceNumber: goldAddressKey,
          districtCode,
          uprn,
          qualifier: goldAddressKey ? 'Gold' : 'Silver'
        };
        const body = createRequestBody(
          "",
          { addressReference, postCode },
          uprnRequest,
          isZen
        );
        return makeRequest(`${BASE_URL}/availability`, {
          method: 'POST',
          body
        });
      };

      const response = await retryAsync(async () => {
        const res = await fetchAvailability();
        if (shouldMakeRequest && !res.availabilityReference) {
          throw new Error('Availability check returned no reference');
        }
        return res;
      });

      let minimumActivationDate = null;
      if (response.broadbandGroups) {
        for (const group of response.broadbandGroups) {
          const product = group.products?.find(p => p.productCode === order.productCode);
          if (product) {
            minimumActivationDate = product.minimumActivationDate || group.minimumActivationDate || null;
            break;
          }
          if (!minimumActivationDate && group.minimumActivationDate) {
            minimumActivationDate = group.minimumActivationDate;
          }
        }
      }

      setAvailabilityRefreshStatus(prev => ({
        ...prev,
        [order.reference]: {
          status: 'success',
          availabilityReference: response.availabilityReference,
          minimumActivationDate,
          lastRefreshed: new Date().toISOString()
        }
      }));

      if (shouldMakeRequest && response.availabilityReference && response.availabilityReference !== 'n/a') {
        dispatch({
          type: "UPDATE_ORDER_AVAILABILITY",
          payload: {
            reference: order.reference,
            availabilityReference: response.availabilityReference,
            lastRefreshed: new Date().toISOString()
          }
        });
      }

      return response;
    } catch (error) {
      setAvailabilityRefreshStatus(prev => ({
        ...prev,
        [order.reference]: {
          status: 'error',
          error: error.message
        }
      }));
      throw error;
    }
  };

  const refreshAllAvailability = async (orders) => {
    setRefreshMessage("Refreshing all availability references...");
    setIsRefreshing(true);

    try {
      const results = await Promise.allSettled(
        orders.map(order => refreshOrderAvailability(order))
      );

      const failedCount = results.filter(r => r.status === 'rejected').length;

      if (failedCount > 0) {
        setRefreshMessage(`Some availability refreshes failed (${failedCount}/${orders.length})`);
      } else {
        setRefreshMessage("All availability references refreshed");
      }
    } catch (error) {
      setRefreshMessage("Failed to refresh availability references");
    } finally {
      setTimeout(() => {
        setRefreshMessage("");
        setIsRefreshing(false);
      }, 3000);
    }
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await fetchPendingOrders();
        const parsed = data
          .filter(order => order.reference && order.reference.toUpperCase().startsWith('ZEN_'))
          .map((order) => {
            try {
              const details = JSON.parse(order.orderPlacement);
              return {
                ...details,
                reference: order.reference,
                partner: order.partner,
                tenant: order.tenant,
              };
            } catch {
              return null;
            }
          }).filter(Boolean);

        dispatch({ type: "SET_DATA", data: parsed });

        refreshAllAvailability(parsed);
      } catch (err) {
        setError("Failed to load pending orders.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [refreshCount]);

  function handleOrderError(err, order, availabilityRef) {
    let errors;
    if (Array.isArray(err)) {
      errors = err.map((e) => e.message || e.Message || JSON.stringify(e));
    } else if (typeof err === "object" && err !== null) {
      errors = Object.values(err).flat().map((m) => (typeof m === "string" ? m : JSON.stringify(m)));
    } else if (typeof err === "string") {
      errors = [err];
    } else {
      errors = [`Failed to place order ${order.reference}`];
    }
    setValidationErrors(errors);
    setOrderToEdit(order);
    setShowRetryMessage(true);
    setExpandedRows((prev) => new Set([...prev, order.reference]));

    if (errors.some((e) => e.toLowerCase().includes("appointment"))) {
      const resolvedRef = availabilityRef
        || availabilityRefreshStatus[order.reference]?.availabilityReference
        || order.availabilityReference;
      lookupAppointment(
        resolvedRef,
        order.productCode,
        order.installationDetails?.goldAddressKey || "",
        order.installationDetails?.districtCode || "",
        "All"
      ).then((response) => {
        if (response?.availableAppointments?.length) {
          const uniqueDates = [
            ...new Set(response.availableAppointments.map((a) => new Date(a.date).toDateString())),
          ].map((ds) => new Date(ds));
          setAppointmentSlots((prev) => ({ ...prev, [order.reference]: uniqueDates }));
        }
      }).catch(() => {});
    }
  }

  async function handleUsernameChange(order, newLocal) {
    const realm =
      usernameOverrides[order.reference]?.realm ??
      order.broadbandCredentials.username.split('@')[1];
    setUsernameOverrides((prev) => ({ ...prev, [order.reference]: { local: newLocal, realm } }));
    setUsernameMessages((prev) => ({ ...prev, [order.reference]: '' }));

    clearTimeout(usernameCheckTimer.current[order.reference]);
    usernameCheckTimer.current[order.reference] = setTimeout(async () => {
      const result = await checkUsernameValid(newLocal, realm);
      setUsernameMessages((prev) => ({ ...prev, [order.reference]: result }));
    }, USERNAME_DEBOUNCE_MS);
  }

  async function handlePlaceOrder(order) {
    setPlacingOrder(order.reference);
    setError("");
    setValidationErrors([]);
    setSuccessMessage("");
    setOrderToEdit(null);
    setShowRetryMessage(false);

    let lastAvailabilityRef = null;

    try {
      const refreshStatus = availabilityRefreshStatus[order.reference];
      if (refreshStatus?.status === 'error') {
        setError(`Availability refresh failed for order ${order.orderReference}. Please try again later.`);
        return;
      }

      const ageMs = refreshStatus?.lastRefreshed
        ? Date.now() - new Date(refreshStatus.lastRefreshed).getTime()
        : Infinity;

      if (ageMs > AVAILABILITY_TTL_MS) {
        try {
          const freshResponse = await refreshOrderAvailability(order);
          lastAvailabilityRef = freshResponse.availabilityReference && freshResponse.availabilityReference !== 'n/a'
            ? freshResponse.availabilityReference
            : order.availabilityReference;
        } catch {
          setError(`Availability refresh failed for order ${order.orderReference}. Please try again later.`);
          return;
        }
      } else {
        lastAvailabilityRef = (refreshStatus?.status === 'success' && refreshStatus.availabilityReference && refreshStatus.availabilityReference !== 'n/a')
          ? refreshStatus.availabilityReference
          : order.availabilityReference;
      }

      const dateOverride = dateOverrides[order.reference] || null;
      const usernameOverride = usernameOverrides[order.reference]
        ? `${usernameOverrides[order.reference].local}@${usernameOverrides[order.reference].realm}`
        : null;
      const javaObject = buildPayload(order, lastAvailabilityRef, dateOverride, usernameOverride);

      const postOrderFn = showDevPanel && devScenario
        ? createDevMock(devScenario)
        : (payload) => postZenOrder(payload);

      if (showDevPanel) {
        setDevLastPayload(javaObject);
        setShowDevPayload(false);
      }

      let zenRef;
      try {
        zenRef = await postOrderFn(JSON.stringify(javaObject));
      } catch {
        const freshResponse = await refreshOrderAvailability(order);
        lastAvailabilityRef = (freshResponse.availabilityReference && freshResponse.availabilityReference !== 'n/a')
          ? freshResponse.availabilityReference
          : order.availabilityReference;
        const retryPayload = buildPayload(order, lastAvailabilityRef, dateOverride, usernameOverride);
        if (showDevPanel) setDevLastPayload(retryPayload);
        zenRef = await postOrderFn(JSON.stringify(retryPayload));
      }

      setSuccessMessage(
        `Order ${order.orderReference} placed successfully (Zen Ref: ${zenRef})`
      );
      setRefreshCount(c => c + 1);

    } catch (err) {
      handleOrderError(err, order, lastAvailabilityRef);
    } finally {
      setPlacingOrder(null);
    }
  }

  async function handleDeclineOrder(order) {
    const cleanedReference = order.reference.replace(/^ZEN_/i, "");

    setDecliningOrder(order.reference);
    setError("");
    setSuccessMessage("");

    try {
      const response = await declineOrder({ partnerReference: cleanedReference });
      setSuccessMessage(
        response.message || `Order ${order.reference} successfully declined`
      );
      setRefreshCount(c => c + 1);
    } catch (err) {
      setError(`Failed to decline order ${order.reference}: ${err.message}`);
    } finally {
      setDecliningOrder(null);
    }
  }

  const { column, data, direction } = state;

  function toggleExpand(ref) {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(ref)) newExpanded.delete(ref);
    else newExpanded.add(ref);
    setExpandedRows(newExpanded);
  }

  return (
    <Segment>
      <Dimmer active={isLoading} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>
      <div className="p-6">
        <h2 className="text-3xl font-bold mb-6">Pending Orders</h2>

        {refreshMessage && (
          <Message
            info
            onDismiss={!isRefreshing ? () => setRefreshMessage("") : null}
          >
            <Icon
              name={isRefreshing ? "spinner" : "check"}
              loading={isRefreshing}
            />
            {refreshMessage}
          </Message>
        )}
        {successMessage && <Message positive>{successMessage}</Message>}
        {error && <Message negative>{error}</Message>}

        {showDevPanel && (
          <Message warning style={{ borderLeft: '4px solid #f39c12' }}>
            <Message.Header>
              <Icon name="lab" /> Dev Test Mode
              <Button
                size="mini"
                basic
                floated="right"
                content="Deactivate"
                onClick={() => { sessionStorage.removeItem('devMode'); setShowDevPanel(false); }}
                style={{ marginTop: '-2px' }}
              />
            </Message.Header>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
              Select a scenario — orders will <strong>not</strong> be sent to ZEN unless "Real request" is selected.
            </p>
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Scenario:</label>
              <select
                value={devScenario}
                onChange={e => { setDevScenario(e.target.value); setDevLastPayload(null); setShowDevPayload(false); }}
                style={{ padding: '0.3rem 0.6rem', fontSize: '0.9rem', borderRadius: '3px', border: '1px solid #ccc' }}
              >
                <option value="">Real request (sends to ZEN)</option>
                <option value="success">✓ Instant success (fake ZEN ref)</option>
                <option value="appointment_error">✗ Appointment error → date picker</option>
                <option value="stale_retry_success">↺ Stale ref → auto-retry succeeds</option>
                <option value="stale_retry_fail">✗ Stale ref → auto-retry also fails</option>
                <option value="validation_errors">✗ Validation errors (array)</option>
                <option value="server_error">✗ Server error</option>
              </select>
            </div>
            {devLastPayload && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                  <strong>Payload — avail ref:</strong>{' '}
                  <code style={{ background: '#fef3cd', padding: '1px 5px', borderRadius: '2px' }}>
                    {devLastPayload.availabilityReference}
                  </code>
                  {devLastPayload.preferredActivationDate && (
                    <>
                      {' · '}
                      <strong>date:</strong>{' '}
                      <code style={{ background: '#fef3cd', padding: '1px 5px', borderRadius: '2px' }}>
                        {new Date(devLastPayload.preferredActivationDate).toLocaleDateString()}
                      </code>
                    </>
                  )}
                </div>
                <Button
                  size="mini"
                  basic
                  onClick={() => setShowDevPayload(p => !p)}
                  content={showDevPayload ? 'Hide full payload' : 'Show full payload'}
                />
                {showDevPayload && (
                  <pre style={{
                    marginTop: '0.5rem',
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    overflowX: 'auto',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}>
                    {JSON.stringify(devLastPayload, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </Message>
        )}

        {validationErrors.length > 0 && (
          <div className="mb-6">
            <Message
              negative
              header="Validation Errors"
              list={validationErrors}
              style={{ whiteSpace: "pre-line" }}
            />
            {showRetryMessage && (
              <Message info>
                <Message.Header>Order Placement Failed</Message.Header>
                <p>
                  The order could not be placed due to validation errors from
                  the supplier.
                </p>
                <p>
                  Please review the order details and re-run the availability
                  check before trying again.
                </p>
                <p>
                  When retrying, make sure to use the original order reference
                  and mark the order as a retry.
                </p>
              </Message>
            )}
          </div>
        )}

        <Table celled striped sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={column === "orderReference" ? direction : null}
                onClick={() =>
                  dispatch({ type: "CHANGE_SORT", column: "orderReference" })
                }
              >
                Order Ref
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === "productCode" ? direction : null}
                onClick={() =>
                  dispatch({ type: "CHANGE_SORT", column: "productCode" })
                }
              >
                Product
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={column === "partner" ? direction : null}
                onClick={() =>
                  dispatch({ type: "CHANGE_SORT", column: "partner" })
                }
              >
                Partner
              </Table.HeaderCell>
              <Table.HeaderCell>Details</Table.HeaderCell>
              <Table.HeaderCell>Place Order</Table.HeaderCell>
              <Table.HeaderCell>Decline Order</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((order) => {
              const isExpanded = expandedRows.has(order.reference);
              const refreshStatus = availabilityRefreshStatus[order.reference];

              return (
                <React.Fragment key={order.reference}>
                  <Table.Row>
                    <Table.Cell>{order.orderReference || "-"}</Table.Cell>
                    <Table.Cell>{order.productCode || "-"}</Table.Cell>
                    <Table.Cell>{order.partner || "-"}</Table.Cell>
                    <Table.Cell
                      onClick={() => toggleExpand(order.reference)}
                      style={{ cursor: "pointer", textAlign: "center" }}
                      data-testid="toggle-details"
                    >
                      <Icon name={isExpanded ? "angle up" : "angle down"} />
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="small"
                        loading={placingOrder === order.reference}
                        disabled={placingOrder === order.reference || !refreshStatus || refreshStatus.status === 'loading'}
                        onClick={() => handlePlaceOrder(order)}
                        content="Place Order"
                        className="button-primary"
                        floated="right"
                        data-testid="place-order-button"
                      />
                      {refreshStatus?.status === 'loading' && (
                        <Icon name="circle notched" loading className="ml-2" />
                      )}
                      {refreshStatus?.status === 'success' && (
                        <Icon
                          name="check circle"
                          color="green"
                          className="ml-2"
                          data-testid="success-icon"
                        />
                      )}
                      {refreshStatus?.status === 'error' && (
                        <Icon
                          name="warning circle"
                          color="red"
                          className="ml-2"
                          data-testid="error-icon"
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="small"
                        loading={decliningOrder === order.reference}
                        disabled={decliningOrder === order.reference}
                        onClick={() => handleDeclineOrder(order)}
                        content="Decline Order"
                        className="button-primary"
                        floated="right"
                        data-testid="decline-order-button"
                      />
                    </Table.Cell>
                  </Table.Row>

                  {isExpanded && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={5}
                        style={{
                          backgroundColor: "#fcfcfc",
                          padding: "1.5rem",
                        }}
                      >
                        <div style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                          <div className="ui divided grid">
                            <div className="row">
                              <div className="eight wide column">
                                {renderIfExists(
                                  order.orderReference,
                                  "Order Reference"
                                )}
                                {renderIfExists(order.partner, "Partner")}
                                {renderIfExists(
                                  order.availabilityReference,
                                  "Availability Reference"
                                )}
                                {order.numberPortAvailabilityReference &&
                                  renderIfExists(
                                    order.numberPortAvailabilityReference,
                                    "Number Port Availability Reference"
                                  )}
                                {refreshStatus?.status === 'success' && (
                                  <>
                                    <strong>Last Refreshed:</strong>{" "}
                                    {new Date(refreshStatus.lastRefreshed).toLocaleString()}
                                    <br />
                                  </>
                                )}
                              </div>
                              <div className="eight wide column">
                                {renderIfExists(
                                  order.productCode,
                                  "Product Code"
                                )}
                                {renderIfExists(
                                  order.pricingOption,
                                  "Pricing Option"
                                )}
                                {renderIfExists(
                                  order.bespokePrice,
                                  "Bespoke Price"
                                )}
                                {renderIfExists(
                                  order.router,
                                  "Selected Router"
                                )}
                                {renderIfExists(
                                  order.isBureauReseller,
                                  "Is Bureau Reseller"
                                )}
                                {renderIfExists(
                                  order.bureauEndCustomer,
                                  "Bureau End Customer"
                                )}
                                {order.contractTerm !== undefined &&
                                  renderIfExists(
                                    order.contractTerm,
                                    "Contract Term"
                                  )}
                                {order.preferredActivationDate && (
                                  <>
                                    <strong>Preferred Activation Date:</strong>{" "}
                                    {orderToEdit?.reference === order.reference &&
                                    validationErrors.some((e) =>
                                      e.toLowerCase().includes("appointment") ||
                                      e.toLowerCase().includes("activation date") ||
                                      e.toLowerCase().includes("preferred activation")
                                    ) ? (
                                      <DatePicker
                                        selected={
                                          dateOverrides[order.reference] ||
                                          new Date(order.preferredActivationDate)
                                        }
                                        onChange={(date) =>
                                          setDateOverrides((prev) => ({
                                            ...prev,
                                            [order.reference]: date,
                                          }))
                                        }
                                        dateFormat="dd/MM/yyyy"
                                        minDate={
                                          appointmentSlots[order.reference]?.length > 0
                                            ? appointmentSlots[order.reference].reduce((earliest, d) => d < earliest ? d : earliest)
                                            : availabilityRefreshStatus[order.reference]?.minimumActivationDate
                                              ? new Date(availabilityRefreshStatus[order.reference].minimumActivationDate)
                                              : new Date()
                                        }
                                        highlightDates={appointmentSlots[order.reference] || []}
                                        includeDates={appointmentSlots[order.reference]?.length > 0 ? appointmentSlots[order.reference] : undefined}
                                        autoComplete="off"
                                      />
                                    ) : (
                                      new Date(order.preferredActivationDate).toLocaleDateString()
                                    )}
                                    <br />
                                  </>
                                )}
                              </div>
                            </div>
                            {/* Installation Details */}
                            {order.installationDetails && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Installation Details
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.installationDetails.phoneNumber,
                                    "Phone Number"
                                  )}
                                  {renderIfExists(
                                    order.installationDetails.goldAddressKey,
                                    "Gold Address Key"
                                  )}
                                  {renderIfExists(
                                    order.installationDetails.districtCode,
                                    "District Code"
                                  )}
                                  {renderIfExists(
                                    order.installationDetails.uprn,
                                    "UPRN"
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {renderAddressIfExists(
                                    order.installationDetails.address
                                  )}
                                </div>
                              </div>
                            )}

                            {/* End User Contact */}
                            {order.endUserContact && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    End User Contact
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.endUserContact.fullName,
                                    "Full Name"
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.endUserContact.phoneNumber,
                                    "Phone Number"
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Broadband Credentials */}
                            {order.broadbandCredentials && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Broadband Credentials
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {orderToEdit?.reference === order.reference &&
                                  validationErrors.some((e) =>
                                    e.toLowerCase().includes("username")
                                  ) ? (
                                    <>
                                      <strong>Username:</strong>{" "}
                                      <input
                                        type="text"
                                        data-testid="username-input"
                                        value={
                                          usernameOverrides[order.reference]?.local ??
                                          order.broadbandCredentials.username.split('@')[0]
                                        }
                                        onChange={(e) =>
                                          handleUsernameChange(order, e.target.value)
                                        }
                                        style={{ width: '130px' }}
                                        autoComplete="off"
                                      />
                                      <span>
                                        @{usernameOverrides[order.reference]?.realm ??
                                          order.broadbandCredentials.username.split('@')[1]}
                                      </span>
                                      {usernameMessages[order.reference] && (
                                        <span
                                          className={
                                            usernameMessages[order.reference] === 'Available'
                                              ? 'login__success'
                                              : 'login__error'
                                          }
                                        >
                                          {" "}{usernameMessages[order.reference]}
                                        </span>
                                      )}
                                      <br />
                                    </>
                                  ) : (
                                    renderIfExists(
                                      order.broadbandCredentials.username,
                                      "Username"
                                    )
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.broadbandCredentials.password,
                                    "Password"
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {order.broadbandCredentials.numberOfIps !==
                                    undefined &&
                                    renderIfExists(
                                      order.broadbandCredentials.numberOfIps,
                                      "Number of IPs"
                                    )}
                                </div>
                              </div>
                            )}

                            <div className="row">
                              <div className="eight wide column">
                                {renderIfExists(
                                  order.accessLineId,
                                  "Access Line ID"
                                )}
                                {order.newSsramsRequired &&
                                  renderIfExists(
                                    order.newSsramsRequired,
                                    "New SSRAMs Required"
                                  )}
                              </div>
                              <div className="eight wide column">
                                {order.workingLineTakeover !== undefined && (
                                  <>
                                    <strong>Working Line Takeover:</strong>{" "}
                                    {order.workingLineTakeover ? "Yes" : "No"}
                                    <br />
                                  </>
                                )}
                                {order.rid && renderIfExists(order.rid, "RID")}
                                {order.linkedOrderReference &&
                                  renderIfExists(
                                    order.linkedOrderReference,
                                    "Linked Order Reference"
                                  )}
                              </div>
                            </div>

                            {/* Options */}
                            {order.options && order.options.length > 0 && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Options
                                  </h4>
                                  {order.options.map((opt, i) => (
                                    <div key={i}>
                                      Type {opt.type}:{" "}
                                      {JSON.stringify(opt.value)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Email Notifications */}
                            {order.emailNotificationsOptIn && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Email Notifications
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {order.emailNotificationsOptIn
                                    .emailNotifications !== undefined && (
                                    <>
                                      <strong>Email Notifications:</strong>{" "}
                                      {order.emailNotificationsOptIn
                                        .emailNotifications
                                        ? "Enabled"
                                        : "Disabled"}
                                      <br />
                                    </>
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {order.emailNotificationsOptIn
                                    .emailAddresses &&
                                    order.emailNotificationsOptIn.emailAddresses
                                      .length > 0 && (
                                      <>
                                        <strong>Email Addresses:</strong>{" "}
                                        {order.emailNotificationsOptIn.emailAddresses.join(
                                          ", "
                                        )}
                                        <br />
                                      </>
                                    )}
                                </div>
                              </div>
                            )}

                            {/* ONT Details */}
                            {order.ontDetails && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    ONT Details
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.ontDetails.ontReference,
                                    "ONT Reference"
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {order.ontDetails.port !== undefined &&
                                    renderIfExists(
                                      order.ontDetails.port,
                                      "Port"
                                    )}
                                </div>
                              </div>
                            )}

                            {/* Realms */}
                            {order.realms && order.realms.length > 0 && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">Realms</h4>
                                  {order.realms.map((realm, i) => (
                                    <div key={i}>{realm.name}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Cellular Details */}
                            {order.cellularDetails && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Cellular Details
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.cellularDetails.iccId,
                                    "ICC ID"
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Appointment */}
                            {order.appointment && (
                              <div className="row">
                                <div className="sixteen wide column">
                                  <h4 className="ui dividing header">
                                    Appointment
                                  </h4>
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.appointment.hazardNotes,
                                    "Hazard Notes"
                                  )}
                                  {renderIfExists(
                                    order.appointment.notes,
                                    "Notes"
                                  )}
                                </div>
                                <div className="eight wide column">
                                  {renderIfExists(
                                    order.appointment.specialArrangementNotes,
                                    "Special Arrangement Notes"
                                  )}
                                  {order.appointment.timeSlot !== undefined &&
                                    renderIfExists(
                                      order.appointment.timeSlot,
                                      "Time Slot"
                                    )}
                                  {order.appointment.appointmentType !==
                                    undefined &&
                                    renderIfExists(
                                      order.appointment.appointmentType,
                                      "Appointment Type"
                                    )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>

      </div>
    </Segment>
  );
}
